import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import {
  WARD_NAME_MAX_LENGTH,
  WARD_SHORT_CODE_LENGTH,
  wardCreateSchema,
  type WardCreateInput,
} from "@handoverpro/shared";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";

export interface WardFormDialogProps {
  mode: "create" | "edit";
  open: boolean;
  /** Pre-fills the fields — empty strings for create, the ward's values for edit. */
  defaultValues: WardCreateInput;
  onSubmit: (values: WardCreateInput) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  /** A server-side error (e.g. a 409 duplicate short code) surfaced above the actions. */
  serverError?: string | null;
}

/**
 * The shared create/edit ward modal (HAN-86). Validates against the same
 * `wardCreateSchema` the API mirrors, so the web form and the server can't drift.
 * The parent owns open/close and the submit→refetch flow; this component just
 * collects + validates the two fields.
 */
export function WardFormDialog({
  mode,
  open,
  defaultValues,
  onSubmit,
  onOpenChange,
  serverError = null,
}: WardFormDialogProps): JSX.Element {
  const form = useForm<WardCreateInput>({
    resolver: zodResolver(wardCreateSchema),
    defaultValues,
    mode: "onBlur",
  });

  // Reset the fields whenever the dialog (re)opens or targets a different ward,
  // so opening "Edit Oncology" never shows the previous ward's values.
  useEffect(() => {
    if (open) form.reset(defaultValues);
    // form.reset is stable; defaultValues is rebuilt per-open by the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultValues]);

  const isSubmitting = form.formState.isSubmitting;
  const title = mode === "create" ? "Create ward" : "Edit ward";

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            The short code is the prefix of every handover code on this ward (e.g.{" "}
            <span className="font-mono">ONC-482</span>).
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ward name</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" maxLength={WARD_NAME_MAX_LENGTH} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="shortCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Short code</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      maxLength={WARD_SHORT_CODE_LENGTH}
                      className="font-mono uppercase"
                      placeholder="ONC"
                      {...field}
                      // Uppercase as the admin types so what they see matches what
                      // is stored (the API also uppercases).
                      onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    />
                  </FormControl>
                  <FormDescription>Exactly 3 letters or digits.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {serverError ? (
              <Alert variant="destructive">
                <AlertTitle>Couldn&rsquo;t save</AlertTitle>
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                {mode === "create" ? "Create ward" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
